(() => {
  const rootSelector = '[data-dog-lp]';
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /**
   * Initializes reveal animations with one shared observer per LP root.
   */
  const initializeReveals = (root) => {
    if (root.dataset.dogLpRevealInitialized === 'true') return;

    const targets = [...root.querySelectorAll('[data-dog-lp-reveal]')];
    if (!targets.length || reducedMotion.matches || !('IntersectionObserver' in window)) {
      targets.forEach((target) => target.classList.add('is-visible'));
      root.dataset.dogLpRevealInitialized = 'true';
      return;
    }

    root.classList.add('dog-lp--motion-ready');

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      },
      {
        rootMargin: '0px 0px -10% 0px',
        threshold: 0.08,
      }
    );

    targets.forEach((target) => observer.observe(target));
    root.dataset.dogLpRevealInitialized = 'true';
  };

  /**
   * Initializes ingredient tabs while preserving native button semantics.
   */
  const initializeTabs = (root) => {
    root.querySelectorAll('[data-dog-lp-tabs]').forEach((tabs) => {
      if (tabs.dataset.dogLpTabsInitialized === 'true') return;

      const triggers = [...tabs.querySelectorAll('[data-dog-lp-tab]')];
      const panels = [...tabs.querySelectorAll('[data-dog-lp-panel]')];

      const activate = (trigger) => {
        const target = trigger.dataset.dogLpTab;

        triggers.forEach((item) => {
          const active = item === trigger;
          item.classList.toggle('is-active', active);
          item.setAttribute('aria-selected', String(active));
          item.tabIndex = active ? 0 : -1;
        });

        panels.forEach((panel) => {
          const active = panel.dataset.dogLpPanel === target;
          panel.hidden = !active;
          panel.classList.toggle('is-active', active);
        });
      };

      triggers.forEach((trigger, index) => {
        trigger.addEventListener('click', () => activate(trigger));
        trigger.addEventListener('keydown', (event) => {
          if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
          event.preventDefault();
          const direction = event.key === 'ArrowRight' ? 1 : -1;
          const nextIndex = (index + direction + triggers.length) % triggers.length;
          triggers[nextIndex].focus();
          activate(triggers[nextIndex]);
        });
      });

      if (triggers[0]) activate(triggers[0]);
      tabs.dataset.dogLpTabsInitialized = 'true';
    });
  };

  /**
   * Initializes sticky CTA visibility after the hero is mostly out of view.
   */
  const initializeStickyCta = (root) => {
    const sticky = root.querySelector('[data-dog-lp-sticky-cta]');
    const hero = root.querySelector('[data-dog-lp-hero]');
    if (!sticky || sticky.dataset.dogLpStickyInitialized === 'true') return;

    if (!hero || !('IntersectionObserver' in window)) {
      sticky.classList.add('is-visible');
      sticky.dataset.dogLpStickyInitialized = 'true';
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        sticky.classList.toggle('is-visible', !entry.isIntersecting);
      },
      {
        threshold: 0.25,
      }
    );

    observer.observe(hero);
    sticky.dataset.dogLpStickyInitialized = 'true';
  };



    /**
   * Loads only the hero video required for the active breakpoint.
   */
  const initializeHeroVideo = (
    root
  ) => {
    root
      .querySelectorAll(
        '[data-dog-lp-hero-media]'
      )
      .forEach((media) => {
        if (
          media.dataset
            .dogLpVideoInitialized ===
          'true'
        ) {
          return;
        }

        const video =
          media.querySelector(
            '[data-dog-lp-hero-video]'
          );

        if (!video) {
          return;
        }

        const desktopQuery =
          window.matchMedia(
            '(min-width: 750px)'
          );

        let activeSource = '';

        const markReady = () => {
          media.classList.add(
            'is-video-ready'
          );
        };

        const clearReady = () => {
          media.classList.remove(
            'is-video-ready'
          );
        };

        const updateVideo = () => {
          if (
            reducedMotion.matches
          ) {
            video.pause();
            clearReady();

            return;
          }

          const desktopSource =
            media.dataset
              .videoDesktop;

          const mobileSource =
            media.dataset
              .videoMobile;

          const nextSource =
            desktopQuery.matches
              ? (
                  desktopSource ||
                  mobileSource
                )
              : (
                  mobileSource ||
                  desktopSource
                );

          if (
            !nextSource ||
            nextSource ===
              activeSource
          ) {
            return;
          }

          clearReady();

          activeSource =
            nextSource;

          video.pause();

          video.src =
            nextSource;

          video.load();

          const playPromise =
            video.play();

          if (
            playPromise &&
            typeof playPromise
              .catch ===
              'function'
          ) {
            playPromise.catch(
              clearReady
            );
          }
        };

        video.addEventListener(
          'playing',
          markReady
        );

        video.addEventListener(
          'error',
          clearReady
        );

        desktopQuery.addEventListener(
          'change',
          updateVideo
        );

        reducedMotion.addEventListener(
          'change',
          updateVideo
        );

        updateVideo();

        media.dataset
          .dogLpVideoInitialized =
          'true';
      });
  };


const initializeRoot = (root) => {
  initializeReveals(root);
  initializeTabs(root);
  initializeStickyCta(root);
  initializeHeroVideo(root);
    initializeHeroButton(root);
};

  /**
   * Initializes the LP mobile menu without coupling behavior to styling classes.
   */
  const initializeHeader = (header) => {
    if (!header || header.dataset.dogLpHeaderInitialized === 'true') return;

    const toggle = header.querySelector('[data-dog-lp-menu-toggle]');
    const menu = header.querySelector('[data-dog-lp-menu]');
    if (!toggle || !menu) return;

    const closeMenu = () => {
      toggle.setAttribute('aria-expanded', 'false');
      toggle.setAttribute('aria-label', 'Open menu');
      menu.hidden = true;
      header.classList.remove('is-menu-open');
    };

    const openMenu = () => {
      toggle.setAttribute('aria-expanded', 'true');
      toggle.setAttribute('aria-label', 'Close menu');
      menu.hidden = false;
      header.classList.add('is-menu-open');
    };

    toggle.addEventListener('click', () => {
      const open = toggle.getAttribute('aria-expanded') === 'true';
      open ? closeMenu() : openMenu();
    });

    header.addEventListener('keyup', (event) => {
      if (event.key === 'Escape') closeMenu();
    });

    menu.addEventListener('click', (event) => {
      if (event.target.closest('a')) closeMenu();
    });

    header.dataset.dogLpHeaderInitialized = 'true';
  };

  const initialize = (scope = document) => {
    scope.querySelectorAll(rootSelector).forEach(initializeRoot);

    const header = scope.matches?.('[data-dog-lp-header]')
      ? scope
      : scope.querySelector?.('[data-dog-lp-header]');

    initializeHeader(header || document.querySelector('[data-dog-lp-header]'));
  };

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  initialize();
})();






const initializeHeroButton = (root) => {
  if (root.dataset.dogLpHeroButtonInitialized === 'true') return;

  root.addEventListener('click', (event) => {
    const button = event.target.closest('[data-dog-lp-hero-button]');

    if (!button || !root.contains(button)) return;

    const href = button.dataset.dogLpHref;
    if (href) window.location.assign(href);
  });

  root.dataset.dogLpHeroButtonInitialized = 'true';
};